/**
 * How each tool appears in the assistant's timeline: what it is doing while the
 * call runs, then what it did once it finishes.
 *
 * These are read by a physician mid-consult, so they are phrased the way a
 * colleague would say what they are doing, not as a log of the operation.
 */
type ToolActivityCopy = { active: string; done: string };

const TOOL_ACTIVITY_COPY: Readonly<Record<string, ToolActivityCopy>> = {
  // KnowledgeBaseSearchToolPlugin
  knowledge_base_search: {
    active: 'Searching the clinical evidence',
    done: 'Searched the clinical evidence',
  },
  faq_search_tool: { active: 'Checking what the guidelines say', done: 'Checked the guidelines' },
  web_search: { active: 'Searching the web for this', done: 'Searched the web' },
  web_fetch: { active: 'Opening a source', done: 'Opened a source' },
  google_places_tool: { active: 'Finding somewhere nearby', done: 'Searched for places nearby' },

  // PatientSearchToolsPlugin
  clinical_notes_search: { active: 'Searching the notes', done: 'Searched the notes' },
  conversation_history_search_tool: {
    active: 'Looking back at what was discussed',
    done: 'Looked back at what was discussed',
  },
  fetch_realtime_inventory: {
    active: 'Seeing what data is available',
    done: 'Checked what data is available',
  },
  fetch_realtime_data: {
    active: 'Pulling in the latest readings',
    done: 'Pulled in the latest readings',
  },

  // Code Mode (PatientTableQueryPlugin)
  run_code: {
    active: 'Working through the details',
    done: 'Worked through the details',
  },

  // ClinicianToolsPlugin
  clinician_helper_tool: {
    active: 'Checking your practice details',
    done: 'Checked your practice details',
  },
  search_medications: { active: 'Looking up the medication', done: 'Looked up the medication' },
  search_pharmacies: { active: 'Finding a pharmacy for them', done: 'Searched for pharmacies' },
  draft_prescription: { active: 'Writing up the prescription', done: 'Wrote up the prescription' },
  search_lab_tests: { active: 'Looking up lab tests', done: 'Looked up lab tests' },
  search_biomarkers: {
    active: 'Checking which markers to include',
    done: 'Checked which markers to include',
  },
  draft_existing_lab: {
    active: 'Putting the lab order together',
    done: 'Put the lab order together',
  },
  search_icd10_codes: {
    active: 'Looking up diagnosis codes',
    done: 'Looked up diagnosis codes',
  },
  send_follow_up: { active: 'Preparing the follow-up', done: 'Prepared the follow-up' },
  debug_invoke_librarian: { active: 'Asking the librarian', done: 'Asked the librarian' },

  // Frontend UI interactions
  clickButton: { active: 'Selecting the action', done: 'Selected the action' },
  updateCheckbox: { active: 'Updating the selection', done: 'Updated the selection' },
  updateTextField: { active: 'Updating the text', done: 'Updated the text' },
  updateSelect: { active: 'Setting that for you', done: 'Set that for you' },
};

// Tools without copy yet still read like a colleague, never like a log entry.
const FALLBACK_COPY: ToolActivityCopy = { active: 'Looking into this', done: 'Looked into this' };

export function getToolActivityText(toolName: string): string {
  return (TOOL_ACTIVITY_COPY[toolName] ?? FALLBACK_COPY).active;
}

export function getToolDoneText(toolName: string): string {
  return (TOOL_ACTIVITY_COPY[toolName] ?? FALLBACK_COPY).done;
}
